import { Icon, ProgressBar } from '@perch-labs/shrike';
import { ImageSelector } from '~/components/image/image-selector';
import { Container } from '~/components/layout';
import { Description } from '~/components/typography';
import {
  DeleteButton,
  ImageContainer,
  PdfImageContainer,
  PreviewContainer,
} from '../image-uploader.style';

export interface ImagePreviewProps {
  onDelete: () => void;
  errorCode?: string;
  base64Image?: string;
  uploaded: boolean;
  isPdf?: boolean;
  name?: string;
  disableDeletion?: boolean;
  validationStatus?: ImagePreviewValidationStatus;
}

export interface ImagePreviewValidationStatus {
  message: string;
  subtitle?: string;
  type: 'error' | 'pending' | 'success' | 'warning';
}

export const ImagePreview = ({
  errorCode,
  onDelete,
  base64Image,
  uploaded,
  isPdf = false,
  name,
  disableDeletion = false,
  validationStatus,
}: ImagePreviewProps) => {
  const status = (uploaded && 'success') || (errorCode && 'error') || 'pending';
  const error = errorCode && (ERROR_CODES[errorCode] || ERROR_CODES['default-code']);
  const isValidating = validationStatus?.type === 'pending';
  const hasValidationError = validationStatus?.type === 'error';
  const hasValidationWarning = validationStatus?.type === 'warning';
  let statusColor: 'error' | 'warningBright' | 'accent4' = 'accent4';
  if (hasValidationError) {
    statusColor = 'error';
  } else if (hasValidationWarning) {
    statusColor = 'warningBright';
  }
  const messageColor = validationStatus?.type === 'success' ? 'accent4' : statusColor;
  let validationTitle = 'Upload successful';
  if (isValidating) {
    validationTitle = 'Validating...';
  } else if (hasValidationError) {
    validationTitle = 'Uploaded, needs review';
  } else if (hasValidationWarning) {
    validationTitle = 'Uploaded, may need review';
  }

  return (
    <PreviewContainer testId="image-preview" my={4}>
      {isPdf ? (
        <PdfImageContainer>
          <ImageSelector type="PDF_FILE" />
        </PdfImageContainer>
      ) : (
        <ImageContainer backgroundImage={base64Image ?? ''} mr={4} />
      )}
      {status === 'pending' && (
        <Container display="flex" flexDirection="column" className="image-preview-details">
          <b>Uploading...</b>
          <ProgressBar maxValue={5000} automatic />
        </Container>
      )}
      {status === 'success' && (
        <Container display="flex" flexDirection="column" className="image-preview-details">
          {isValidating ? (
            <b>{validationTitle}</b>
          ) : (
            <>
              <Container display="flex" alignItems="center">
                <Icon
                  icon={
                    hasValidationError || hasValidationWarning
                      ? 'ExclamationCircleInverted'
                      : 'CheckmarkCircleInverted'
                  }
                  color={statusColor}
                  margin={{ right: '8px' }}
                />
                <b>{validationTitle}</b>
              </Container>
              {!!name && <div className="file-name">{name}</div>}
              {!!validationStatus?.message && (
                <Description color={messageColor} fontWeight="bold" lineHeight="150%" mb={0} mt={2}>
                  {validationStatus.message}
                </Description>
              )}
              {!!validationStatus?.subtitle && (
                <Description color="secondary" lineHeight="150%" mb={0} mt={1}>
                  {validationStatus.subtitle}
                </Description>
              )}
            </>
          )}
        </Container>
      )}
      {status === 'error' && (
        <Container className="image-preview-details">
          <Description color="error" fontWeight="bold" lineHeight="150%" mb={0}>
            {error!.reason}
          </Description>
          <Description mb={0}>{error!.description}</Description>
        </Container>
      )}
      {status !== 'pending' && (
        <DeleteButton
          onClick={() => onDelete()}
          data-testid="delete-upload-button"
          disabled={disableDeletion}
        >
          <Icon icon="UIDelete" title="delete-image" opacity={disableDeletion ? 'low' : 'high'} />
        </DeleteButton>
      )}
    </PreviewContainer>
  );
};

export const ERROR_CODES = {
  'already-uploaded': { reason: 'Already uploaded', description: 'Upload a different file' },
  'file-invalid-type': { reason: 'Unsupported format', description: 'Try a JPG, PNG, or HEIC' },
  'file-too-large': { reason: 'File is too large', description: 'Must be ≤ 5MB' },
  'max-files-exceeded': { reason: 'Upload failed', description: 'Exceeded max number of files' },
  'save-failed': { reason: 'Failed to save', description: 'Try uploading again' },
  'too-many-files': { reason: 'Upload failed', description: 'Exceeded max number of files' },
  'default-code': { reason: 'Upload failed', description: 'Try a different image' },
};

export type UploadErrorCode = keyof typeof ERROR_CODES;
